import React, { useState } from 'react'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import {
  IconArrowLeft,
  IconMicrophone,
  IconMicrophoneOff,
  IconPhone,
  IconPhoneOff,
} from '@tabler/icons-react'
import { Button } from '@/components/buttons/Button'
import { TextInput } from '@/components/forms/TextInput'

import { useWebCallContext } from '../hooks/WebCallContext'
import { useAgentDisplayName } from '../hooks/useAgentDisplayName'
import { useAgentIdentifiers } from '../hooks/useAgentIdentifiers'
import { useAgentOrb } from '../hooks/useAgentOrb'
import { useSlideDock } from '../hooks/useSlideDock'
import { conicWash, orbRing } from '../orbPalettes'

import CallOrb from './CallOrb'
import CallConversation from './CallConversation'
import CallHistoryPanel from './CallHistoryPanel'

const STATUS_LABEL = {
  idle: 'Ready to call',
  connecting: 'Connecting…',
  live: 'Connected — start talking',
  ended: 'Call ended',
  error: 'Call failed',
}

// The call, full screen: history on the left, the agent in the middle, the
// conversation on the right.
//
// It began as a column beside the canvas. Full screen instead, because once
// the transcript is here there are three things to read at once and none of
// them fit in a rail — and because a call is a mode you are in, not a panel
// you have open.
const WebCallScreen = () => {
  const {
    status,
    error,
    isMuted,
    turns,
    history,
    isScreenOpen,
    toggleMute,
    startCall,
    hangUp,
    closeScreen,
    getLevels,
  } = useWebCallContext()
  const selectedAgentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const palette = useAgentOrb()
  const { agentUserId, organizationId } = useAgentIdentifiers()
  const { displayName } = useAgentDisplayName()
  const { mounted, entered, ref } = useSlideDock(isScreenOpen)

  // `null` means the live call. A key means you are reading a past one.
  const [selectedKey, setSelectedKey] = useState(null)
  // The number this call should present as. A browser has none of its own, and
  // most agents key their CRM lookup on the caller's number — westwood/mvp
  // takes it as the ANI verbatim — so without this a web call cannot reach any
  // of that logic. Blank is fine and common: the agent then sees the same
  // Studio marker it always did.
  const [callerNumber, setCallerNumber] = useState('')

  if (!mounted) {
    return null
  }

  const isBusy = status === 'connecting' || status === 'live'
  const isFailed = status === 'error'
  const selectedCall = selectedKey ? history.find((call) => call.key === selectedKey) : null
  const shownTurns = selectedCall ? selectedCall.turns : turns

  // Dialling drops any past call you were reading. Without this the new call
  // goes live while `selectedCall` still resolves to the historic entry, so the
  // panel keeps showing the old transcript until you click "Live call".
  const handleDial = () => {
    setSelectedKey(null)
    startCall(selectedAgentId, callerNumber.trim() || null)
  }

  return (
    <div
      ref={ref}
      className={classNames('voice-agents__call-screen', {
        'voice-agents__call-screen--open': entered,
      })}
      data-testid="web-call-screen"
      role="dialog"
      // Full-screen and covering the workspace, so background content should be
      // inert to assistive tech as well as visually.
      aria-modal="true"
      aria-label="Web call"
    >
      <header className="voice-agents__call-screen-head">
        <Button
          text
          icon={<IconArrowLeft />}
          className="voice-agents__call-screen-back"
          onClick={closeScreen}
          data-testid="web-call-close-button"
        >
          Back
        </Button>

        {/* The agent, and nothing else. Everything on this screen is about one
            call with one agent, so the header only has to say which — and it
            sits directly above the blob it names. */}
        <div className="voice-agents__call-screen-identity">
          <span
            className="voice-agents__call-screen-orb"
            style={{ boxShadow: orbRing(palette) }}
            aria-hidden="true"
          >
            <span
              className="voice-agents__call-screen-orb-wash"
              style={{ background: conicWash(palette, 32) }}
            />
          </span>
          <span className="voice-agents__call-screen-name">{displayName || 'Agent'}</span>
        </div>

        {/* Mirrors the conversation rail, so the identity lands over the blob
            rather than over the middle of the screen — see the SCSS. */}
        <span className="voice-agents__call-screen-head-spacer" aria-hidden="true" />
      </header>

      <div className="voice-agents__call-screen-body">
        <CallHistoryPanel
          history={history}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          isCallLive={isBusy}
          agentIdentity={{ agentUserId, organizationId }}
        />

        <div className="voice-agents__call-screen-stage">
          <CallOrb palette={palette} getLevels={getLevels} animating={isBusy}>
            {isBusy ? (
              <button
                type="button"
                className="voice-agents__call-screen-hangup"
                onClick={hangUp}
                aria-label="End call"
                data-testid="web-call-hangup-button"
              >
                <IconPhoneOff size={18} />
              </button>
            ) : (
              /* Opening this view does not dial. The call starts here, on a
                 deliberate press — landing in a screen that is already ringing
                 gives you nothing to decide. */
              <button
                type="button"
                className="voice-agents__call-screen-dial"
                onClick={handleDial}
                disabled={!selectedAgentId}
                aria-label="Start call"
                data-testid="web-call-start-button"
              >
                <IconPhone size={18} />
              </button>
            )}
          </CallOrb>

          <p
            className={
              isFailed
                ? 'voice-agents__call-screen-status voice-agents__call-screen-status--error'
                : 'voice-agents__call-screen-status'
            }
            data-testid="web-call-status"
          >
            {isFailed ? error || STATUS_LABEL.error : STATUS_LABEL[status] || ''}
          </p>

          {/* Idle only: changing the number mid-call would say nothing about
              the call in progress, which was placed with the old one. */}
          {!isBusy && (
            <label className="voice-agents__call-screen-as" htmlFor="call-as-number">
              <span>Call as</span>
              <TextInput
                small
                id="call-as-number"
                type="tel"
                placeholder="+15551234567 (optional)"
                value={callerNumber}
                onChange={(event) => setCallerNumber(event.target.value)}
                data-testid="web-call-caller-number"
              />
            </label>
          )}

          {isBusy && (
            <button
              type="button"
              className="voice-agents__call-screen-mute"
              onClick={toggleMute}
              disabled={status !== 'live'}
              data-testid="web-call-mute-button"
            >
              {isMuted ? <IconMicrophoneOff size={16} /> : <IconMicrophone size={16} />}
              {isMuted ? 'Unmute' : 'Mute'}
            </button>
          )}
        </div>

        <CallConversation turns={shownTurns} isBusy={isBusy} isHistoric={Boolean(selectedCall)} />
      </div>
    </div>
  )
}

export default WebCallScreen
